import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { selectZefDialogState, zefDialogClose } from '@zerops/zef/dialog';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { ServiceStackBaseApi } from '@zerops/zerops/core/service-stack-base/service-stack-base.api';
import { EMPTY, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { EmbeddedUiOverlayComponent, openInNewTabWhenResolved } from '../embedded-ui-overlay';
import { FEATURE_NAME } from './service-ui-overlay.constant';
import { ServiceUiOverlayMeta } from './service-ui-overlay.model';
import { ServiceUiOverlayService } from './service-ui-overlay.service';

// The access token is consumed once by the login redirect, after that the session lives in the proxy cookie.
// So the URL is only fetched when a frame is created or explicitly reloaded, never to "refresh" a frame that is alive.
@Component({
  selector: 'z-service-ui-overlay',
  templateUrl: './service-ui-overlay.feature.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ EmbeddedUiOverlayComponent ]
})
export class ServiceUiOverlayFeature extends ZefReactiveComponent {

  // # Deps
  #store = inject(Store);
  #api = inject(ServiceStackBaseApi);
  overlay = inject(ServiceUiOverlayService);

  // # Event Streams
  onClose$ = new Subject<void>();
  onReload$ = new Subject<void>();
  onOpenInNewTab$ = new Subject<void>();

  // # Data
  // -- sync
  key = FEATURE_NAME;
  url = signal<string | null>(null);
  serviceStackName = signal('');
  loading = signal(false);
  #loadedServiceStackId: string | null = null;

  // -- async
  dialogState$ = this.#store.pipe(select(selectZefDialogState(FEATURE_NAME)));
  open$ = this.dialogState$.pipe(map((data) => !!data?.state));
  openMeta$ = this.dialogState$.pipe(
    filter((data) => !!data?.state),
    map((data) => data.meta as ServiceUiOverlayMeta | undefined),
    filter((meta): meta is ServiceUiOverlayMeta => !!meta?.serviceStackId)
  );
  serviceStackId$ = this.openMeta$.pipe(map((meta) => meta.serviceStackId));

  // # Dispatcher
  constructor() {
    super();

    this.$dispatchActions([
      this.onClose$.pipe(
        tap(() => this.#reset()),
        map(() => zefDialogClose({ key: FEATURE_NAME }))
      ),

      // Re-opening for the service whose frame is alive only brings it forward, so the embedded app keeps its session storage.
      this.openMeta$.pipe(
        tap((meta) => {
          this.overlay.open.set(true);
          this.overlay.restore();
          this.serviceStackName.set(meta.serviceStackName || '');
        }),
        filter((meta) => !(this.#loadedServiceStackId === meta.serviceStackId && !!this.url())),
        tap((meta) => this.#loadedServiceStackId = meta.serviceStackId),
        switchMap((meta) => this.#load$(meta.serviceStackId))
      ),

      // Navigates the existing frame to a fresh login URL, which also recovers a session whose token expired.
      this.onReload$.pipe(
        filter(() => !!this.#loadedServiceStackId),
        switchMap(() => this.#load$(this.#loadedServiceStackId))
      ),

      // A fresh access each time. The tab is opened before the request so popup blockers let it through.
      this.onOpenInNewTab$.pipe(
        withLatestFrom(this.serviceStackId$),
        switchMap(([ _, id ]) => openInNewTabWhenResolved(
          this.#api.uiAccess$(id).pipe(map((access) => access.urlUi))
        ))
      ),

      // Dialog closed from elsewhere
      this.open$.pipe(
        filter((open) => !open),
        tap(() => this.#reset()),
        map(() => null),
        filter((v) => v !== null)
      )
    ]);
  }

  // On failure the previous URL stays, so a failed reload leaves the live frame alone and only the first load shows the empty state.
  #load$(serviceStackId: string) {
    this.loading.set(true);
    return this.#api.uiAccess$(serviceStackId).pipe(
      tap((access) => {
        this.loading.set(false);
        this.url.set(access.urlUi);
      }),
      catchError(() => {
        this.loading.set(false);
        return EMPTY;
      }),
      map(() => null),
      filter((v): v is never => v !== null)
    );
  }

  #reset() {
    this.overlay.reset();
    this.url.set(null);
    this.serviceStackName.set('');
    this.loading.set(false);
    this.#loadedServiceStackId = null;
  }

}
